import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';

/**
 * ngLet
 * This directive is used to control the value
 * based on *ngIf source code.
 *
 * ngIf will not render the element if used with an
 * observable which emits falsy values. But if we wanted
 * avoid this limitation and do some manual checks on the
 * falsy values emitted by the observable, we can use the ngLet alternative.
 *
 * Example:
 * ngIf directive has some limitations, for an example,
 *  <div *ngIf="roleData | async as role">
 * 	    <a *ngIf="role === 0">Duplicate</a>
 * 	    <a *ngIf="role === 1">Move</a>
 * 	    <a *ngIf="role === 2">Rename</a>
 *  </div>
 *
 * However, if the role value is 0, ngIf directive will
 * think this as a false value. null, undefined, 0, false
 * all considered as false value by ngIf.
 *
 * But we need that value to do some condition checks.
 * We can use ngLet which will render the falsy value as well.
 *  <div *ngLet="roleData | async as role">
 * 	    <a *ngIf="role === 0">Duplicate</a>
 * 	    <a *ngIf="role === 1">Move</a>
 * 	    <a *ngIf="role === 2">Rename</a>
 *  </div>
 *
 * @author  vinoch
 * @since   2019-11-19
 */

interface ILetContext<T> {
    ngLet: T;
}

@Directive({
    selector: '[ngLet]',
})
export class NgLet<T> {
    private _context: ILetContext<T> = { ngLet: null };

    constructor( _viewContainer: ViewContainerRef, _templateRef: TemplateRef<ILetContext<T>> ) {
        _viewContainer.createEmbeddedView( _templateRef, this._context );
    }

    @Input()
    set ngLet( value: T ) {
        this._context.ngLet = value;
    }
}
